import AspApi from 'api/ApiConfig';

export const BrandsList = async (payload) => {
  const res = await AspApi.get(`/api/v1/brand/users?brand_id=${payload}`);
  return res;
};

/**
 * Generates an API key for the specified brand
 * @param {Object} payload - The payload containing brand information
 * @param {string} payload.brand_id - The unique identifier for the brand (required)
 * @returns {Promise<Object>} The API response containing the generated API key
 * @throws {Error} If brand_id is missing or API request fails
 */
export const generateApiKey = async (payload) => {
  if (!payload?.brand_id) {
    throw new Error("Brand ID is required to generate API key");
  }

  try {
    const response = await AspApi.post('api/v1/brands/create_api_key', payload);
    return response.data;
  } catch (error) {
    console.error('Failed to generate API key:', error);
    throw new Error(error.response?.data?.message || 'Failed to generate API key');
  }
};

/**
 * Fetches all API keys for a specific brand
 * @param {number} brandId - The ID of the brand to fetch API keys for
 */
export const fetchBrandApiKeys = async (brandId) => {
  if (!brandId) {
    throw new Error("Brand ID is required to fetch API keys");
  }

  try {
    const response = await AspApi.get('api/v1/brands/api_keys', {
      params: { brand_id: brandId }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch API keys:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch API keys');
  }
};


export const fetchKeywords = async (brandId, params = {}) => {
  if (!brandId) {
    throw new Error("Brand ID is required to fetch keywords");
  }

  const queryParams = new URLSearchParams();
  queryParams.append('brand_id', brandId);
  
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.search) queryParams.append('search', params.search);
  
  // Handle table filters and sorting
  if (params.status) queryParams.append('status', params.status);
  if (params.matching_method) {
    queryParams.append('match_type', params.matching_method === 'Exact Match' ? 'exact' : 'fuzzy');
  }
  if (params.trigger_type) queryParams.append('trigger_type', params.trigger_type);
  
  // Handle sorting
  if (params.sortField) {
    const sortOrder = params.sortOrder === 'descend' ? 'desc' : 'asc';
    queryParams.append('sort_by', `${params.sortField}:${sortOrder}`);
  }

  try {
    const response = await AspApi.get(`/api/v1/svc/campaign_sequence/keywords?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch keywords:', error);
    throw new Error(error?.response?.data?.message || 'Failed to fetch keywords');
  }
};

export const deleteKeyword = async (brandId, payload) => {
  if (!brandId) {
    throw new Error("Brand ID is required to delete keyword");
  }

  if (!payload?.id) {
    throw new Error("Keyword ID is required to delete keyword");
  }

  try {
    const response = await AspApi.delete(`/api/v1/svc/campaign_sequence/keywords/${payload?.id}?brand_id=${brandId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete keyword:', error);
    throw new Error(error || 'Failed to delete keyword');
  }
};

export const updateKeywordStatus = async (brandId, payload) => {
  if (!brandId) {
    throw new Error("Brand ID is required to update keyword status");
  }

  if (!payload?.id) {
    throw new Error("Keyword ID is required to update keyword status");
  }

  try {
    const response = await AspApi.patch(`/api/v1/svc/campaign_sequence/keywords/${payload?.id}/update_status?brand_id=${brandId}`, payload);
    return response.data;
  } catch (error) {
    console.error('Failed to update keyword status:', error);
    throw new Error(error || 'Failed to update keyword status');
  }
};
